import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Full card data from original sticky cards
const cardData = [
  {
    level: 'L1',
    name: 'Assisted',
    category: 'TOOL',
    attributes: [
      { text: 'Human Owned', ai: false },
      { text: 'Human Coded', ai: false },
      { text: 'Human Operated', ai: false },
    ],
    description: 'Traditional setup. A developer builds the agent and triggers every action manually. The AI executes tasks on command but never acts on its own.',
    integration: [
      'Agent gets a wallet on any activated chain',
      'Developer triggers transactions through API calls',
      'Full spending limits and manual approval gates',
      'Every transaction requires a human signature',
    ],
    example: 'A trading bot the developer triggers manually to execute specific swaps on Robinhood Network.',
    glassIntensity: 0.02,
    borderIntensity: 0.05,
  },
  {
    level: 'L2',
    name: 'Semi-Auto',
    category: 'ASSISTANT',
    attributes: [
      { text: 'Human Owned', ai: false },
      { text: 'Human Coded', ai: false },
      { text: 'AI Operated', ai: true },
    ],
    description: 'The agent runs on its own within boundaries the developer sets. It can make decisions and act 24/7, but the rules are human-defined.',
    integration: [
      'Agent autonomously signs transactions within set limits',
      'Spending caps and per-tx maximums enforced at wallet level',
      'Automated recurring purchases (AI credits, compute top-ups)',
      'Owner gets webhook alerts and can freeze wallet anytime',
    ],
    example: 'A DeFi yield agent that rebalances portfolios and purchases its own compute credits.',
    glassIntensity: 0.03,
    borderIntensity: 0.06,
  },
  {
    level: 'L3',
    name: 'Autonomous',
    category: 'AUTONOMOUS',
    attributes: [
      { text: 'Human Owned', ai: false },
      { text: 'AI Coded', ai: true },
      { text: 'AI Operated', ai: true },
    ],
    description: 'The agent writes its own logic and operates independently. The human retains ownership but doesn\'t control day-to-day decisions.',
    integration: [
      'Agent manages its own multi-chain wallet portfolio',
      'Self-directed token swaps, bridges, and credit purchases',
      'Dynamic spending limits that adjust based on earnings',
      'Agent-to-agent payment rails for hiring sub-agents',
    ],
    example: 'An AI that writes trading strategies, deploys them, earns revenue, and reinvests autonomously.',
    glassIntensity: 0.04,
    borderIntensity: 0.07,
  },
  {
    level: 'L4',
    name: 'Sovereign',
    category: 'SOVEREIGN',
    attributes: [
      { text: 'AI Owned', ai: true },
      { text: 'AI Coded', ai: true },
      { text: 'AI Operated', ai: true },
    ],
    description: 'Fully autonomous entities with no human in the loop. The agent owns its wallets, earns its income, and evolves without intervention.',
    integration: [
      'Self-custodied wallets with no human key-holder',
      'Full economic independence across 20+ chains',
      'Autonomous purchasing of AI credits, compute, and services',
      'Agent issues its own sub-wallets for spawned child agents',
    ],
    example: 'A self-sustaining AI that earns from DeFi yields, pays for Claude API credits, and funds new agents.',
    glassIntensity: 0.06,
    borderIntensity: 0.09,
  },
];

// Rotating text ring SVG component - now fully visible
function RotatingTextRing() {
  const ringText = "HOVER TO EXPLORE · L1 ASSISTED · L2 SEMI-AUTO · L3 AUTONOMOUS · L4 SOVEREIGN · HOVER TO EXPLORE · L1 ASSISTED · L2 SEMI-AUTO · L3 AUTONOMOUS · L4 SOVEREIGN · ";
  
  return (
    <svg 
      className="text-ring absolute top-0 left-0 w-full h-full pointer-events-none z-[1]"
      viewBox="0 0 420 420"
      data-testid="rotating-text-ring"
    >
      <defs>
        <path 
          id="text-circle"
          d="M 210,210 m -185,0 a 185,185 0 1,1 370,0 a 185,185 0 1,1 -370,0"
          fill="none"
        />
      </defs>
      <text className="ring-text">
        <textPath href="#text-circle" startOffset="0%">
          {ringText}
        </textPath>
      </text>
    </svg>
  );
}

// Expanded card modal/overlay with full content
function ExpandedCard({ data, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
      />
      
      {/* Card */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 10 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[580px] rounded-2xl overflow-hidden"
        style={{
          background: `rgba(255,255,255,${data.glassIntensity + 0.02})`,
          border: `1px solid rgba(255,255,255,${data.borderIntensity + 0.04})`,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow: '0 0 80px rgba(255,255,255,0.05), 0 25px 80px rgba(0,0,0,0.5)',
        }}
        data-testid={`expanded-card-${data.level.toLowerCase()}`}
      >
        {/* Close hint */}
        <div className="absolute top-4 right-4 text-[10px] text-white/30 uppercase tracking-wider">
          Click outside to close
        </div>
        
        <div className="p-8 sm:p-10">
          {/* Header: Level + Category */}
          <div className="flex justify-between items-baseline mb-6">
            <motion.span 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-[48px] sm:text-[56px] font-black text-white leading-none"
            >
              {data.level}
            </motion.span>
            <motion.span 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="text-[11px] font-semibold uppercase tracking-[0.08em] text-white/50"
            >
              {data.category}
            </motion.span>
          </div>

          {/* Divider */}
          <motion.hr 
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.15, duration: 0.4 }}
            className="border-none h-px bg-white/[0.08] mb-6 origin-left"
          />

          {/* Attributes */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex flex-wrap gap-1.5 text-[13px] sm:text-[14px] font-medium mb-5"
          >
            {data.attributes.map((attr, i) => (
              <span key={i} className="flex items-center">
                <span className={attr.ai ? 'text-white' : 'text-white/35'}>
                  {attr.text}
                </span>
                {i < data.attributes.length - 1 && (
                  <span className="text-white/20 mx-2">·</span>
                )}
              </span>
            ))}
          </motion.div>

          {/* Description */}
          <motion.p 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="text-[14px] sm:text-[15px] text-white/60 leading-[1.7] mb-6"
          >
            {data.description}
          </motion.p>

          {/* Divider */}
          <motion.hr 
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="border-none h-px bg-white/[0.08] mb-6 origin-left"
          />

          {/* Integration section */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
          >
            <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.08em] text-white/40">
              How AIBL Integrates
            </span>
            
            <ul className="mt-4 space-y-2">
              {data.integration.map((item, i) => (
                <motion.li 
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.05 }}
                  className="text-[13px] sm:text-[14px] text-white/70 leading-[1.7] pl-5 relative"
                >
                  <span className="absolute left-0 text-white/30">→</span>
                  {item}
                </motion.li>
              ))}
            </ul>

            {/* Example */}
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-[12px] sm:text-[13px] text-white/40 italic mt-5 pl-5 border-l border-white/10"
            >
              <span className="text-white/50 not-italic">Example:</span> {data.example}
            </motion.p>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// Individual agent card component
function AgentCard({ data, index, onExpand }) {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      className={`agent-card card-l${index + 1}`}
      style={{
        background: `rgba(255,255,255,${data.glassIntensity})`,
        border: `1px solid rgba(255,255,255,${data.borderIntensity})`,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onExpand(data)}
      whileHover={{ scale: 1.08 }}
      whileTap={{ scale: 0.98 }}
      data-testid={`agent-card-l${index + 1}`}
    >
      {/* Level number */}
      <motion.span 
        className="card-level"
        animate={{ 
          color: isHovered ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.15)',
          scale: isHovered ? 1.1 : 1
        }}
      >
        {data.level}
      </motion.span>
      
      {/* Level name */}
      <motion.span 
        className="card-level-name"
        animate={{ 
          color: isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)'
        }}
      >
        {data.name}
      </motion.span>
      
      {/* Hint to click */}
      <motion.span
        className="absolute bottom-3 left-0 right-0 text-center text-[9px] uppercase tracking-wider text-white/0"
        animate={{ 
          color: isHovered ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0)'
        }}
      >
        Click to explore
      </motion.span>
      
      {/* Glow effect on hover */}
      <motion.div
        className="absolute inset-0 rounded-2xl pointer-events-none"
        animate={{
          boxShadow: isHovered 
            ? '0 0 30px rgba(255,255,255,0.08), inset 0 0 20px rgba(255,255,255,0.02)'
            : '0 0 0px rgba(255,255,255,0), inset 0 0 0px rgba(255,255,255,0)'
        }}
        transition={{ duration: 0.3 }}
      />
    </motion.div>
  );
}

// Mobile version - tappable cards
function MobileAgentTypes({ onExpand }) {
  return (
    <div className="grid grid-cols-2 gap-3 px-4 max-w-sm mx-auto">
      {cardData.map((data, index) => (
        <motion.div
          key={index}
          className="p-5 rounded-xl text-center cursor-pointer"
          style={{
            background: `rgba(255,255,255,${data.glassIntensity})`,
            border: `1px solid rgba(255,255,255,${data.borderIntensity})`,
          }}
          onClick={() => onExpand(data)}
          whileTap={{ scale: 0.95 }}
          data-testid={`agent-card-mobile-l${index + 1}`}
        >
          <div className="text-3xl font-extrabold text-white/20 tracking-tight">{data.level}</div>
          <div className="text-[11px] font-medium text-white/50 uppercase tracking-wide mt-1">{data.name}</div>
          <div className="text-[9px] text-white/30 mt-3 uppercase tracking-wider">Tap to explore</div>
        </motion.div>
      ))}
    </div>
  );
}

export default function AgentTypes() {
  const [isMobile, setIsMobile] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [expandedCard, setExpandedCard] = useState(null);
  const sectionRef = useRef(null);

  // Check for mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 900);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Intersection observer for reveal animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Lock body scroll when card is expanded
  useEffect(() => {
    if (expandedCard) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [expandedCard]);

  return (
    <>
      {/* CSS Styles */}
      <style>{`
        /* Rotating text ring animation */
        @keyframes ring-spin {
          to { transform: rotate(360deg); }
        }

        .text-ring {
          animation: ring-spin 30s linear infinite;
        }

        .ring-text {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.2em;
          fill: rgba(255,255,255,0.6);
          font-family: system-ui, sans-serif;
        }

        /* Slide in animations */
        @keyframes slide-in-left {
          to { opacity: 1; transform: translateX(0); }
        }

        @keyframes slide-in-right {
          to { opacity: 1; transform: translateX(0); }
        }

        .agent-types-text {
          opacity: 0;
          transform: translateX(-30px);
        }

        .agent-types-visual {
          opacity: 0;
          transform: translateX(30px);
        }

        .agent-types-section.visible .agent-types-text {
          animation: slide-in-left 0.6s ease forwards;
        }

        .agent-types-section.visible .agent-types-visual {
          animation: slide-in-right 0.6s ease 0.2s forwards;
        }

        /* Card grid */
        .agent-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          grid-template-rows: 1fr 1fr;
          gap: 14px;
          width: 300px;
          height: 300px;
          position: relative;
          z-index: 2;
        }

        /* Cards base styles */
        .agent-card {
          border-radius: 20px;
          position: relative;
          overflow: hidden;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          transition: background 0.3s ease, border-color 0.3s ease;
        }

        .agent-card:hover {
          background: rgba(255,255,255,0.08) !important;
          border-color: rgba(255,255,255,0.15) !important;
        }

        .card-level {
          font-size: 36px;
          font-weight: 800;
          letter-spacing: -0.04em;
          transition: all 0.3s ease;
        }

        .card-level-name {
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-top: 4px;
          transition: all 0.3s ease;
        }

        /* Sibling dimming on grid hover */
        .agent-grid:hover .agent-card:not(:hover) {
          opacity: 0.4;
          filter: blur(0.5px);
        }

        /* Ring slows down when hovering grid */
        .agent-types-visual:has(.agent-grid:hover) .text-ring {
          animation-duration: 60s;
        }

        /* Responsive */
        @media (max-width: 900px) {
          .agent-types-layout {
            flex-direction: column !important;
            text-align: center;
            gap: 60px !important;
          }

          .agent-types-text {
            max-width: 100% !important;
            text-align: center;
          }

          .agent-types-visual {
            width: 360px !important;
            height: 360px !important;
          }

          .agent-grid {
            width: 240px !important;
            height: 240px !important;
            gap: 12px !important;
          }
        }

        @media (max-width: 480px) {
          .agent-types-visual {
            width: 320px !important;
            height: 320px !important;
          }

          .agent-grid {
            width: 220px !important;
            height: 220px !important;
            gap: 10px !important;
          }

          .card-level {
            font-size: 28px !important;
          }

          .ring-text {
            font-size: 9px !important;
          }
        }

        /* Reduced motion */
        @media (prefers-reduced-motion: reduce) {
          .text-ring {
            animation: none;
          }
        }
      `}</style>

      <section 
        ref={sectionRef}
        id="agent-types"
        className={`agent-types-section bg-[#0A0A0A] py-[120px] px-6 overflow-hidden ${isVisible ? 'visible' : ''}`}
        data-testid="agent-types-section"
      >
        <div 
          className="agent-types-layout max-w-[1100px] mx-auto flex items-center justify-between gap-20"
          style={{ flexDirection: isMobile ? 'column' : 'row' }}
        >
          {/* Left column: Text */}
          <div className="agent-types-text flex-shrink-0" style={{ maxWidth: isMobile ? '100%' : '420px' }}>
            {/* Badge */}
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block text-[12px] font-semibold uppercase tracking-[0.08em] text-white/60 px-4 py-1.5 rounded-full mb-6"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              Agent Types
            </motion.span>

            {/* Headline */}
            <h2 
              className="text-[36px] font-bold text-white leading-[1.15] tracking-[-0.02em]"
              data-testid="agent-types-title"
            >
              For every level of<br />AI autonomy.
            </h2>

            {/* Body */}
            <p className="text-[15px] text-white/45 leading-[1.7] mt-5">
              From basic transaction signers to fully autonomous operators that manage their own portfolios, AIBL provides the infrastructure for every level.
            </p>

            {/* CTA */}
            <a 
              href="#docs" 
              className="inline-block mt-8 px-5 py-2.5 rounded-lg text-[13px] font-medium text-white/70 transition-all duration-250 hover:text-white hover:border-white/30 hover:bg-white/[0.04]"
              style={{
                border: '1px solid rgba(255,255,255,0.15)',
              }}
              data-testid="agent-types-cta"
            >
              Explore Docs →
            </a>
          </div>

          {/* Right column: Grid + Ring */}
          {!isMobile ? (
            <div 
              className="agent-types-visual relative flex-shrink-0 flex items-center justify-center"
              style={{ width: '440px', height: '440px' }}
            >
              {/* Rotating text ring - now fully visible */}
              <RotatingTextRing />

              {/* 2x2 card grid */}
              <div className="agent-grid">
                {cardData.map((data, index) => (
                  <AgentCard 
                    key={data.level} 
                    data={data} 
                    index={index}
                    onExpand={setExpandedCard}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="agent-types-visual w-full">
              <MobileAgentTypes onExpand={setExpandedCard} />
            </div>
          )}
        </div>
      </section>

      {/* Expanded card modal */}
      <AnimatePresence>
        {expandedCard && (
          <ExpandedCard 
            data={expandedCard} 
            onClose={() => setExpandedCard(null)} 
          />
        )}
      </AnimatePresence>
    </>
  );
}
